import { useQuery } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api-client'

/** Sidebar ve DashboardPage aynı anahtarı paylaşır: tek istek dedupe edilir. */
export const DASHBOARD_ACCESS_QUERY_KEY = ['dashboard-access'] as const

interface DashboardAccessResponse {
  success?: boolean
  data?: { allowed?: unknown; connected?: unknown }
}

/**
 * GET /api/dashboard/access — yeni ana ekranı bu kullanıcı görebilir mi?
 * Yanıt KATI ayrıştırılır (fail-closed, D-06): yalnız `success === true` ve
 * `data.allowed === true` iken true. Bozuk gövde, string/1 gibi değerler false.
 * Ağ/HTTP hatası fırlatılır; kanca bunu allowed:false'a çevirir.
 */
export async function fetchDashboardAccess(viewAs?: string | null): Promise<{ allowed: boolean; connected: boolean }> {
  // #883: yönetici bir satıcıyı salt-okur görürken `connected` HEDEF satıcıya göre gelir.
  const url = viewAs ? `/dashboard/access?viewAs=${encodeURIComponent(viewAs)}` : '/dashboard/access'
  const res = await apiFetch<DashboardAccessResponse | null>(url)
  return {
    allowed: res?.success === true && res.data?.allowed === true,
    // Yalnız AÇIKÇA false ise bağlı değil: bayrak yoksa/bozuksa eski davranış (kenar menü düğmeleri görünür).
    connected: res?.data?.connected !== false,
  }
}

export interface UseDashboardAccessOptions {
  /**
   * true: yalnız ÖNBELLEĞİ oku (kenar menü). Bağlanınca yeni istek atmaz; yalnız
   * hiç veri yokken (soğuk önbellek) tek istek atılır. Hata sonrası da her
   * bağlanışta yeniden denemez. Sayfa açılışındaki taze okuma (D-11) DashboardPage'in işi.
   */
  cacheOnly?: boolean
  /**
   * #883: yöneticinin salt-okur baktığı satıcının bağlantı kimliği (yalnız geçerli uuid + yönetici
   * iken verilir). `connected` hedef satıcının bağlantı durumunu yansıtır; ayrı önbellek anahtarı
   * kullanır, yöneticinin kendi erişim önbelleğine karışmaz. Yoksa davranış değişmez.
   */
  viewAs?: string | null
}

/**
 * Erişim bilgisi sayfa açılışında TAZE okunur (D-11): staleTime 0 ve
 * refetchOnMount 'always'. Hata/yükleniyor durumu asla `allowed: true` üretmez.
 *
 * `resolvedForThisMount`: BU bileşen bağlandıktan sonra en az bir yanıt (başarı
 * ya da hata) geldi mi? Sayfa yalnız bunu bekler; arka plan yenilemesi
 * (başka bileşenin bağlanması, invalidate) ekranı iskelete çevirip sökmez.
 */
export function useDashboardAccess(opts: UseDashboardAccessOptions = {}): {
  isPending: boolean
  resolvedForThisMount: boolean
  allowed: boolean
  /** Amazon hesabı bağlı mı (kenar menüdeki "Panelim" seçimi için). Bilinmeyen = true. */
  connected: boolean
} {
  const cacheOnly = opts.cacheOnly === true
  const viewAs = opts.viewAs ?? null
  const query = useQuery({
    queryKey: viewAs ? ([...DASHBOARD_ACCESS_QUERY_KEY, viewAs] as const) : DASHBOARD_ACCESS_QUERY_KEY,
    queryFn: () => fetchDashboardAccess(viewAs),
    staleTime: 0,
    refetchOnMount: cacheOnly ? false : 'always',
    retryOnMount: !cacheOnly,
    refetchOnWindowFocus: false,
    // Ağ geri gelince iskelete dönüp açık ekranı söküp takmasın; sunucu 403 zaten zorlar.
    refetchOnReconnect: false,
    retry: false,
  })
  return {
    isPending: query.status === 'pending',
    resolvedForThisMount: query.isFetchedAfterMount,
    allowed: query.status === 'success' && query.data.allowed === true,
    connected: query.status !== 'success' || query.data.connected !== false,
  }
}
